/**
 * web/src/App.tsx
 * Route table + auth gate. Nav is organized by workload, not by the engine's
 * "workflow" concept (2026-07 restructure — there is no generic Workflows tab):
 *   /login                                    — public (Plan 07)
 *   /                                          → /sponsors redirect
 *   /sponsors                                  — Sponsor onboarding list (UI-03; sponsor-scoped WorkflowList)
 *   /sponsors/approvals                        — Sponsor approval queue (UI-05, family-scoped)
 *   /workflows                                 → /sponsors redirect (legacy bookmarks)
 *   /workflows/:id                             — Workflow Detail (UI-04 — canonical deep link for BOTH types)
 *   /workflows/:id/gate/:step                  — Gate Review (UI-06, Plan 09)
 *   /speakers                                  — Speaker roster (flag-gated)
 *   /speakers/approvals                        — Speaker approval queue (flag-gated, family-scoped)
 *   /speakers/:eventId/:itemId                 — Speaker hub / timeline (flag-gated)
 *   /approvals                                 → /sponsors/approvals redirect (legacy bookmarks)
 *   /approvals/:workflowId/:stepIndex          — Gate Review (queue → review path)
 *   /confirmations                             — Confirmations (UI-07, Plan 10)
 *   /events                                    — Event Management (UI-08, admin only — Plan 10)
 *
 * The nested <Outlet /> structure under <AppShell /> means the shell
 * renders once for every authed route and pages mount inside it.
 *
 * Auth gate via useAuth(): on /login show the Login page; everywhere else
 * require an authed session — 401s are intercepted by queryClient and
 * redirect to /login?expired=1. The `RequireAuth` wrapper handles the
 * initial render race where /api/me is still pending (returns the
 * AppShell skeleton instead of flashing a redirect).
 *
 * Role-based access: client-side filters live in <RoleGuard> + the
 * sidebar's adminOnly nav item; the server's requireRole middleware
 * (src/auth/middleware.ts) is the authoritative authorization
 * (T-05-06-01 — non-admin who navigates to /events directly sees the
 * page render but the underlying PUT/POST events routes 403 server-side).
 */
import type { ReactNode } from 'react';
import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { usePostHog } from './lib/posthog';
import { AppShell } from './components/layout/AppShell';
import { FlagGuard } from './components/layout/FlagGuard';
import { FLAGS } from './lib/flags';
import { SkeletonList } from './components/shared/Skeleton';
import { Login } from './pages/Login';
import { WorkflowList } from './pages/WorkflowList';
import { GateReview } from './pages/GateReview';
import { ApprovalQueue } from './pages/ApprovalQueue';
import { Confirmations } from './pages/Confirmations';
import { UserAccess } from './pages/UserAccess';
import { Settings } from './pages/Settings';

// Heavy, rarely-hit routes are code-split so they don't ship in the initial
// bundle (EventConfigure ~1300 lines, WorkflowDetail ~420, EventManagement is
// admin-only). Pages use named exports, so each dynamic import is mapped to
// `default` for React.lazy. The root <ErrorBoundary> (main.tsx) already
// catches a failed chunk fetch; the <Suspense> below shows a skeleton list in
// the content area while the chunk loads.
const WorkflowDetail = lazy(() =>
  import('./pages/WorkflowDetail').then((m) => ({ default: m.WorkflowDetail })),
);
const EventManagement = lazy(() =>
  import('./pages/EventManagement').then((m) => ({ default: m.EventManagement })),
);
const EventConfigure = lazy(() =>
  import('./pages/EventConfigure').then((m) => ({ default: m.EventConfigure })),
);
const BrandManagement = lazy(() =>
  import('./pages/BrandManagement').then((m) => ({ default: m.BrandManagement })),
);
// Speaker onboarding (flag-gated). Code-split — never shipped in the initial
// bundle, and the routes are wrapped in <FlagGuard> so they redirect when the
// workflow-speaker-onboarding flag is off (the server also 404s the API).
const SpeakerRoster = lazy(() =>
  import('./pages/SpeakerRoster').then((m) => ({ default: m.SpeakerRoster })),
);
const SpeakerHub = lazy(() =>
  import('./pages/SpeakerHub').then((m) => ({ default: m.SpeakerHub })),
);
// Speaker Approvals — action-first list + review queue overlay (absorbs the
// retired BatchGateReview / CardReviewStack stacks). Flag-gated + code-split.
const SpeakerApprovals = lazy(() =>
  import('./pages/SpeakerApprovals').then((m) => ({ default: m.SpeakerApprovals })),
);
// Speaker Cards pipeline board — per-stage artwork review
// (design_handoff_speaker_cards). Flag-gated + code-split.
const SpeakerCards = lazy(() =>
  import('./pages/SpeakerCards').then((m) => ({ default: m.SpeakerCards })),
);
// Accommodation pickup report (flag-gated). Code-split — never shipped in the
// initial bundle; <FlagGuard> redirects when the accommodations flag is off.
const Accommodations = lazy(() =>
  import('./pages/Accommodations').then((m) => ({ default: m.Accommodations })),
);
// Accommodation email agent (flag-gated, shadow-only — the agent never sends).
const AccommodationsEmailAgent = lazy(() =>
  import('./pages/AccommodationsEmailAgent').then((m) => ({
    default: m.AccommodationsEmailAgent,
  })),
);
// Sponsor ROI reports (flag-gated). Batch-generate per-sponsor recap decks from
// a Personatech export. Code-split — never in the initial bundle.
const SponsorRoi = lazy(() =>
  import('./pages/SponsorRoi').then((m) => ({ default: m.SponsorRoi })),
);
// Wave 2 ops automations (flag-gated): the five SOP flows + their run review page.
const OpsAutomations = lazy(() =>
  import('./pages/OpsAutomations').then((m) => ({ default: m.OpsAutomations })),
);
const OpsRunReview = lazy(() =>
  import('./pages/OpsRunReview').then((m) => ({ default: m.OpsRunReview })),
);
// Content Intelligence — the searchable session library over transcribed
// conference recordings. Flag-gated (content-intelligence — the server 404s
// the whole content API when off); past the flag, visible to any
// authenticated user.
// Code-split — the library + transcript views never ship in the initial bundle.
const Content = lazy(() => import('./pages/Content').then((m) => ({ default: m.Content })));
const ContentSession = lazy(() =>
  import('./pages/ContentSession').then((m) => ({ default: m.ContentSession })),
);
const ContentSpeaker = lazy(() =>
  import('./pages/ContentSpeaker').then((m) => ({ default: m.ContentSpeaker })),
);

/**
 * Wraps an authed branch — redirects to /login if /api/me 401s. The
 * queryClient subscriber already redirects on 401 globally; this guard
 * handles the initial render race where me is still pending so the user
 * sees the AppShell skeleton instead of a redirect flash.
 *
 * @param children content to render once me is loaded successfully
 * @sideEffects subscribes to the /api/me query via useAuth
 * @idempotent yes — multiple mounts share the React Query cache entry
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { me, isPending, error } = useAuth();
  const location = useLocation();
  // Init posthog-js + identify once /api/me resolves (no-op when PostHog is
  // unconfigured). Called above the early-returns so hook order stays stable.
  usePostHog(me);
  if (isPending) {
    // Skeleton shell — full visual frame loads while /api/me resolves.
    return <AppShell pending />;
  }
  if (error || !me) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  }
  return <>{children}</>;
}

/**
 * Top-level route table. Mounted under <BrowserRouter> in main.tsx.
 *
 * @sideEffects none directly; child routes mount queries / mutations
 * @idempotent yes
 */
export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/sponsors" replace />} />
        <Route path="sponsors" element={<WorkflowList />} />
        <Route path="sponsors/approvals" element={<ApprovalQueue />} />
        {/* Legacy bookmarks/deep links to the old list tab. The :id detail
            routes below stay canonical for both workflow types. */}
        <Route path="workflows" element={<Navigate to="/sponsors" replace />} />
        <Route
          path="workflows/:id"
          element={
            <Suspense fallback={<SkeletonList />}>
              <WorkflowDetail />
            </Suspense>
          }
        />
        <Route path="workflows/:id/gate/:step" element={<GateReview />} />
        <Route
          path="speakers"
          element={
            <FlagGuard flag={FLAGS.speakerOnboarding} fallback={<Navigate to="/sponsors" replace />}>
              <Suspense fallback={<SkeletonList />}>
                <SpeakerRoster />
              </Suspense>
            </FlagGuard>
          }
        />
        {/* Speaker Approvals — action-first list + review queue overlay
            (design_handoff_speaker_approvals). Static segment, so it never
            collides with the :eventId/:itemId hub route below. */}
        <Route
          path="speakers/approvals"
          element={
            <FlagGuard
              flag={FLAGS.speakerOnboarding}
              fallback={<Navigate to="/sponsors/approvals" replace />}
            >
              <Suspense fallback={<SkeletonList />}>
                <SpeakerApprovals />
              </Suspense>
            </FlagGuard>
          }
        />
        {/* Speaker Cards pipeline board — static segment, so it must precede
            the :eventId/:itemId hub route below ("cards" is not an event id). */}
        <Route
          path="speakers/cards"
          element={
            <FlagGuard flag={FLAGS.speakerOnboarding} fallback={<Navigate to="/sponsors" replace />}>
              <Suspense fallback={<SkeletonList />}>
                <SpeakerCards />
              </Suspense>
            </FlagGuard>
          }
        />
        {/* The retired Batch Review + Illustration Review stacks — absorbed by
            the Speaker Approvals review queue. Redirects keep old bookmarks. */}
        <Route path="speakers/batch-review" element={<Navigate to="/speakers/approvals" replace />} />
        <Route path="speakers/card-review" element={<Navigate to="/speakers/approvals" replace />} />
        <Route
          path="speakers/:eventId/:itemId"
          element={
            <FlagGuard flag={FLAGS.speakerOnboarding} fallback={<Navigate to="/sponsors" replace />}>
              <Suspense fallback={<SkeletonList />}>
                <SpeakerHub />
              </Suspense>
            </FlagGuard>
          }
        />
        <Route
          path="accommodations"
          element={
            <FlagGuard flag={FLAGS.accommodations} fallback={<Navigate to="/sponsors" replace />}>
              <Suspense fallback={<SkeletonList />}>
                <Accommodations />
              </Suspense>
            </FlagGuard>
          }
        />
        <Route
          path="ops"
          element={
            <FlagGuard flag={FLAGS.opsAutomations} fallback={<Navigate to="/sponsors" replace />}>
              <Suspense fallback={<SkeletonList />}>
                <OpsAutomations />
              </Suspense>
            </FlagGuard>
          }
        />
        <Route
          path="ops/runs/:workflowId"
          element={
            <FlagGuard flag={FLAGS.opsAutomations} fallback={<Navigate to="/sponsors" replace />}>
              <Suspense fallback={<SkeletonList />}>
                <OpsRunReview />
              </Suspense>
            </FlagGuard>
          }
        />
        <Route
          path="sponsor-roi"
          element={
            <FlagGuard flag={FLAGS.sponsorRoi} fallback={<Navigate to="/sponsors" replace />}>
              <Suspense fallback={<SkeletonList />}>
                <SponsorRoi />
              </Suspense>
            </FlagGuard>
          }
        />
        <Route
          path="accommodations/email-agent"
          element={
            <FlagGuard
              flag={FLAGS.accommodationsEmailAgent}
              fallback={<Navigate to="/sponsors" replace />}
            >
              <Suspense fallback={<SkeletonList />}>
                <AccommodationsEmailAgent />
              </Suspense>
            </FlagGuard>
          }
        />
        {/* Content Intelligence — library + hybrid search + Ask, then the
            per-session detail + full transcript (?seg= deep-links a moment).
            Flag-gated: the server 404s the content API when off. */}
        <Route
          path="content"
          element={
            <FlagGuard flag={FLAGS.contentIntelligence} fallback={<Navigate to="/sponsors" replace />}>
              <Suspense fallback={<SkeletonList />}>
                <Content />
              </Suspense>
            </FlagGuard>
          }
        />
        {/* Static segment — must precede content/:sessionId so "speakers" is
            not swallowed as a session id. */}
        <Route
          path="content/speakers/:name"
          element={
            <FlagGuard flag={FLAGS.contentIntelligence} fallback={<Navigate to="/sponsors" replace />}>
              <Suspense fallback={<SkeletonList />}>
                <ContentSpeaker />
              </Suspense>
            </FlagGuard>
          }
        />
        <Route
          path="content/:sessionId"
          element={
            <FlagGuard flag={FLAGS.contentIntelligence} fallback={<Navigate to="/sponsors" replace />}>
              <Suspense fallback={<SkeletonList />}>
                <ContentSession />
              </Suspense>
            </FlagGuard>
          }
        />
        {/* Legacy bookmark — the queue now lives under each area's group. */}
        <Route path="approvals" element={<Navigate to="/sponsors/approvals" replace />} />
        <Route path="approvals/:workflowId/:stepIndex" element={<GateReview />} />
        <Route path="confirmations" element={<Confirmations />} />
        <Route
          path="events"
          element={
            <Suspense fallback={<SkeletonList />}>
              <EventManagement />
            </Suspense>
          }
        />
        <Route
          path="events/:id/configure"
          element={
            <Suspense fallback={<SkeletonList />}>
              <EventConfigure />
            </Suspense>
          }
        />
        <Route
          path="admin/brands"
          element={
            <Suspense fallback={<SkeletonList />}>
              <BrandManagement />
            </Suspense>
          }
        />
        <Route path="admin/access" element={<UserAccess />} />
        <Route path="settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/sponsors" replace />} />
      </Route>
    </Routes>
  );
}
